import { authenticatedFetch } from '../../api/apiClient';
import { isSafeHttpUrl } from '../shortcuts/shortcutUrl';
import { openShortcutInBrowser } from '../shortcuts/openShortcutUrl';

const BASE = '/api/v2';

export class AskSbApiError extends Error {
  constructor(message, { detail = '', status = 0 } = {}) {
    super(message || 'Ask Signal Bridges request failed');
    this.name = 'AskSbApiError';
    this.detail = detail || '';
    this.status = status || 0;
  }
}

async function readJsonSafe(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

/**
 * JSON client for Ask SB routes. The bearer token stays in the Authorization header.
 * It is never added to the URL, and response bodies are not written to storage.
 */
async function sendJson(path, method, body, { signal } = {}) {
  const response = await authenticatedFetch(`${BASE}${path}`, {
    method,
    signal,
    headers: {
      Accept: 'application/json',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await readJsonSafe(response);
  if (!response.ok || payload.success === false) {
    throw new AskSbApiError(payload.error || `Request failed (${response.status})`, {
      detail: payload.detail || '',
      status: response.status,
    });
  }
  return payload.data && typeof payload.data === 'object' ? payload.data : payload;
}

export function getAskSbAccess() {
  return sendJson('/ask-sb/access', 'GET');
}

/** Superadmin tenant entitlement. Call these only from the tenant detail page. */
export function getAskSbEntitlement(tenantId) {
  const id = encodeURIComponent(String(tenantId || ''));
  return sendJson(`/admin/tenants/${id}/ask-sb/entitlement`, 'GET');
}

export function putAskSbEntitlement(tenantId, enabled) {
  const id = encodeURIComponent(String(tenantId || ''));
  return sendJson(`/admin/tenants/${id}/ask-sb/entitlement`, 'PUT', { enabled: Boolean(enabled) });
}

export function getAskSbIntegration() {
  return sendJson('/ask-sb/integration', 'GET');
}

export function putAskSbIntegration(body) {
  return sendJson('/ask-sb/integration', 'PUT', body);
}

export function storeOpenAiKey(apiKey) {
  return sendJson('/ask-sb/openai/key', 'POST', { apiKey });
}

export function deleteOpenAiKey() {
  return sendJson('/ask-sb/openai/key', 'DELETE');
}

export function testOpenAi() {
  return sendJson('/ask-sb/openai/test', 'POST');
}

export function connectDrive() {
  return sendJson('/ask-sb/drive/connect', 'POST');
}

export function testDrive() {
  return sendJson('/ask-sb/drive/test', 'POST');
}

export function disconnectDrive() {
  return sendJson('/ask-sb/drive/disconnect', 'POST');
}

export function putAskSbGrant(userId, granted) {
  return sendJson('/ask-sb/grants', 'PUT', { userId, granted: Boolean(granted) });
}

export function searchAskSb(query, options) {
  return sendJson('/ask-sb/search', 'POST', { query }, options);
}

export function readAskSb(sourceHandle, options) {
  return sendJson('/ask-sb/read', 'POST', { sourceHandle }, options);
}

export function askAskSb(body, options) {
  return sendJson('/ask-sb/ask', 'POST', body, options);
}

export function cancelAskSb(requestId) {
  return sendJson('/ask-sb/cancel', 'POST', { requestId });
}

export async function fetchAskSbPreview(sourceHandle, { signal } = {}) {
  const handle = encodeURIComponent(String(sourceHandle || ''));
  const response = await authenticatedFetch(`${BASE}/ask-sb/preview/${handle}`, {
    method: 'GET',
    signal,
    headers: { Accept: 'application/pdf, text/plain, application/json' },
  });
  const kind = String(response.headers.get('X-Ask-Sb-Preview') || '').trim();
  if (!response.ok) {
    const payload = await readJsonSafe(response);
    throw new AskSbApiError(payload.error || 'Preview failed', {
      detail: payload.detail || '',
      status: response.status,
    });
  }
  if (kind === 'extracted_text') {
    return { kind: 'extracted_text', text: await response.text() };
  }
  if (kind === 'original') {
    const blob = await response.blob();
    const pdf = blob.type === 'application/pdf'
      ? blob
      : new Blob([blob], { type: 'application/pdf' });
    return { kind: 'original', blob: pdf };
  }
  const payload = await readJsonSafe(response);
  throw new AskSbApiError(payload.error || 'This file cannot be previewed', {
    detail: payload.detail || '',
    status: response.status,
  });
}

/**
 * Open a source link in the browser. javascript: and other non-http(s) URLs are rejected
 * by the shared shortcut helper before any window is opened.
 */
export async function openAskSbOriginal(url) {
  if (!isSafeHttpUrl(url)) {
    throw new Error('Only http and https links can be opened');
  }
  return openShortcutInBrowser(url);
}

function userBelongsToTenant(user, tenantId) {
  const ids = [user?.tenantId, user?.tenant_id, user?.claims?.tenantId].filter(Boolean);
  if (!ids.length) return true;
  return ids.includes(tenantId);
}

export async function listAskSbTenantUsers(tenantId) {
  if (!tenantId) {
    throw new AskSbApiError('Tenant is required');
  }
  const response = await authenticatedFetch(`/users/list?tenantId=${encodeURIComponent(tenantId)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const payload = await readJsonSafe(response);
  if (!response.ok || payload.success === false) {
    throw new AskSbApiError(payload.error || payload.message || 'Could not load users', {
      detail: payload.detail || '',
      status: response.status,
    });
  }
  const data = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  const users = Array.isArray(data)
    ? data
    : Array.isArray(data.users)
      ? data.users
      : Array.isArray(payload.users)
        ? payload.users
        : [];
  return users.filter((user) => userBelongsToTenant(user, tenantId));
}
